import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SaenggibuForm from './SaenggibuForm';
import LoginPrompt from '../../Auth/LoginPrompt';
import { useAuth } from '../../contexts/AuthContext';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';

import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';
import { ClassFlowDemo, SaenggibuResultDemo } from '../../components/demo';

const CreateSaenggibu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationKey = location.key || 'initial';
  const entryStateKeyRef = React.useRef(locationKey);
  const entryStateValueRef = React.useRef(location.state || null);
  const isConsumingBatchRestoreRef = React.useRef(false);
  const entryInitialState = (
    entryStateKeyRef.current === locationKey || isConsumingBatchRestoreRef.current
  )
    ? entryStateValueRef.current
    : (location.state || null);
  const [error, setError] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // 교사 설정 관련 상태 간소화 - 사용자 경험 개선을 위해 단순화
  const [teacherSettings, setTeacherSettings] = useState({ default_instructions: '' });
  
  // UI 모드 상태 (단일 생성 vs 학급 전체 생성)
  const [uiMode, setUiMode] = useState('quick');
  // 다중 세션 토큰: 페이지 진입(locationKey)마다 새로 만들어진다. 모드 토글로는 바뀌지 않는다(아래 handleModeClick 참고).
  const batchResetToken = `${locationKey}:batch`;
  
  // 🆕 첫 방문 감지 (모드 선택 버튼 강조용)
  const [showModeHint, setShowModeHint] = useState(() => {
    return !localStorage.getItem('saenggibu-mode-seen');
  });
  
  // 🆕 파일 첨부 버튼 강조 (첫 방문 시)
  const [showFileHint, setShowFileHint] = useState(() => {
    return !localStorage.getItem('saenggibu-file-hint-seen');
  });
  
  // 🆕 스크롤 및 하이라이트를 위한 refs
  const modeButtonsRef = React.useRef(null);
  const fileAttachRef = React.useRef(null);
  
  const auth = useAuth();
  
  useEffect(() => {
    // 세션 스토리지 초기화
    sessionStorage.setItem('saenggibuSource', 'self-created');
  }, []);

  useEffect(() => {
    setError(null);
    setShowLoginPrompt(false);
  }, [location.key]);

  useEffect(() => {
    const routeState = location.state || null;
    if (routeState?.batchRestore) {
      entryStateKeyRef.current = locationKey;
      entryStateValueRef.current = routeState;
      isConsumingBatchRestoreRef.current = true;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (isConsumingBatchRestoreRef.current) {
      entryStateKeyRef.current = locationKey;
      isConsumingBatchRestoreRef.current = false;
      return;
    }
    entryStateKeyRef.current = locationKey;
    entryStateValueRef.current = routeState;
  }, [location.pathname, location.state, locationKey, navigate]);
  
  // 🆕 첫 방문 힌트 자동 숨김 (5초 후)
  useEffect(() => {
    if (showModeHint) {
      const timer = setTimeout(() => {
        setShowModeHint(false);
        localStorage.setItem('saenggibu-mode-seen', 'true');
      }, 6000); // 6초 후 자동 숨김
      
      return () => clearTimeout(timer);
    }
  }, [showModeHint]);
  
  useEffect(() => {
    if (showFileHint) {
      const timer = setTimeout(() => {
        setShowFileHint(false);
        localStorage.setItem('saenggibu-file-hint-seen', 'true');
      }, 6000); // 6초 후 자동 숨김
      
      return () => clearTimeout(timer);
    }
  }, [showFileHint]);
  
  // 🆕 모드 변경 핸들러
  // 단일↔다중 토글은 다중 세션(batchResetToken)을 갈아엎지 않는다. 결과 확인 중 잠깐 단일로 다녀와도
  // 다중 결과·설정이 그대로 남아야 한다(프로덕션: 세션 내 단일→다중 전환 860건). 새 세션은
  // 메뉴 재진입(locationKey 변경) 또는 결과 화면의 "설정도 초기화하고 새로 시작"으로만 만든다.
  const handleModeClick = (mode) => {
    // 모드 전환은 허용 (파일 업로드 시 PRO 체크)
    if (mode !== uiMode) {
      trackSaenggibuUxEvent('saenggibu_mode_switch', { from_mode: uiMode, to_mode: mode, is_batch: mode === 'batch' });
    }
    setUiMode(mode);
    setShowModeHint(false);
    localStorage.setItem('saenggibu-mode-seen', 'true');
  };

  const handleUiModeChange = (mode) => {
    if (mode !== uiMode) {
      trackSaenggibuUxEvent('saenggibu_mode_switch', { from_mode: uiMode, to_mode: mode, is_batch: mode === 'batch' });
    }
    setUiMode(mode);
  };
  
  // 🆕 스크롤 및 하이라이트 함수
  const scrollToModeButtons = () => {
    if (modeButtonsRef.current) {
      modeButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 하이라이트 효과
      modeButtonsRef.current.classList.add('highlight-pulse');
      setTimeout(() => {
        modeButtonsRef.current?.classList.remove('highlight-pulse');
      }, 2000);
    }
  };
  
  const scrollToFileAttach = () => {
    if (fileAttachRef.current) {
      fileAttachRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 하이라이트 효과
      fileAttachRef.current.classList.add('highlight-pulse');
      setTimeout(() => {
        fileAttachRef.current?.classList.remove('highlight-pulse');
      }, 2000);
    }
  };

  // 교사 설정 관련 로직 제거됨 - 사용자 경험 개선을 위해 단순화
  // 이제 교사 설정 없이도 고품질 생기부 생성이 가능합니다
  
  // 교사 설정 기본값으로 고정
  useEffect(() => {
    setTeacherSettings({ default_instructions: '' });
  }, [setTeacherSettings]);

  // 같은 방문(history entry)에서는 한 번만 다중 생성으로 연다. 그 뒤 선생님이 단일 생성으로 바꾸면 그대로 둔다.
  const batchEntryAppliedRef = React.useRef(null);
  useEffect(() => {
    const batchFromLink = new URLSearchParams(location.search).get('mode') === 'batch';
    if (!(location.state?.batchRestore || location.state?.startBatch || batchFromLink)) return;
    if (batchEntryAppliedRef.current === locationKey) return;
    batchEntryAppliedRef.current = locationKey;
    setUiMode('batch');
    setShowModeHint(false);
    localStorage.setItem('saenggibu-mode-seen', 'true');
  }, [location.state, location.search, locationKey]);

  // 로그인 프롬프트 처리
  const handleLoginPromptClose = () => {
    setShowLoginPrompt(false);
  };

  // 교사 설정 로딩 제거됨 - 즉시 사용 가능한 생기부 생성

  return (
    <>
      <style>{saenggibuPageStyles}</style>
      <div className="saenggibu-page-container" style={{ position: 'relative' }}>
        <Helmet>
          <title>생기부 AI 자동 작성 | 세특·행발 3분 완성 | 2026 기재요령 반영 | 왓퀴즈</title>
          <meta name="description" content={`AI로 생기부를 3분 만에 작성하세요. 2026 기재요령 반영, 세부능력특기사항·행동발달상황 초안 생성. 현직 교사 ${TEACHER_COUNT_LABEL}이 사용하는 생기부 AI.`} />
          <meta name="keywords" content="생기부 AI, 생활기록부 자동작성, 세부능력특기사항, 행동발달상황, 2026 기재요령, 나이스 생기부, 교사 AI, 세특 AI, 행발 AI, 생기부 자동 작성" />
          <link rel="canonical" href="https://whatquiz.co.kr/create-saenggibu" />
          <meta property="og:title" content="생기부 AI 자동 작성 | 세특·행발 3분 완성" />
          <meta property="og:description" content={`AI로 생기부를 3분 만에 작성. 2026 기재요령 완벽 반영. 현직 교사 ${TEACHER_COUNT_LABEL}이 사용하는 생기부 AI.`} />
          <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
          <meta property="og:url" content="https://whatquiz.co.kr/create-saenggibu" />
          <meta property="og:type" content="website" />
        </Helmet>

        {/* 헤더 + 모드 버튼 (같은 줄로 컴팩트화) */}
        <div className="saenggibu-page-header">
          <div className="saenggibu-header-row">
            <h1 className="saenggibu-page-title">생기부 작성</h1>
            <div 
              ref={modeButtonsRef}
              className="saenggibu-mode-toggle"
            >
              <button
                onClick={() => handleModeClick('quick')}
                className={`saenggibu-mode-btn ${uiMode === 'quick' ? 'saenggibu-mode-btn--active-quick' : ''}`}
              >
                단일 생성
              </button>
              <button
                onClick={() => handleModeClick('batch')}
                className={`saenggibu-mode-btn ${uiMode === 'batch' ? 'saenggibu-mode-btn--active-batch' : ''}`}
              >
                다중 생성
              </button>
            </div>
          </div>
          <p className="saenggibu-page-description">
            7시간 걸리던 생기부, 이제 3분이면 끝 · 2026 기재요령 기반
          </p>
        </div>

        {/* 비로그인 사용자: 결과 화면 예시 (Value First) */}
        {!auth.currentUser && (
          <div className="sgb-preview-card">
            {uiMode === 'batch' ? (
              <ClassFlowDemo surface="create_saenggibu" title="반 전체 생기부" />
            ) : (
              <SaenggibuResultDemo surface="create_saenggibu" title="생기부 작성" />
            )}
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div className="saenggibu-error-message">
            {error}
          </div>
        )}

        {/* 메인 콘텐츠 */}
        <SaenggibuForm
          key={`saenggibu-entry-${locationKey}`}
          auth={auth}
          teacherSettings={teacherSettings}
          uiMode={uiMode}
          setUiMode={handleUiModeChange}
          batchResetToken={batchResetToken}
          showFileHint={showFileHint}
          fileAttachRef={fileAttachRef}
          scrollToModeButtons={scrollToModeButtons}
          scrollToFileAttach={scrollToFileAttach}
          initialState={entryInitialState}
          onError={setError}
          onLoginRequired={() => {
            setShowLoginPrompt(true);
          }}
        />

        {/* 로그인 프롬프트 모달 */}
        {showLoginPrompt && (
          <LoginPrompt
            isOpen={showLoginPrompt}
            onClose={handleLoginPromptClose}
            feature="saenggibu"
          />
        )}
      </div>
    </>
  );
};

// 토스 스타일 CSS
const saenggibuPageStyles = `
  /* 메인 컨테이너 */
  .saenggibu-page-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 12px 20px;
    font-family: var(--whatquiz-font-family);
  }

  /* 헤더 - 컴팩트 */
  .saenggibu-page-header {
    margin-bottom: 8px;
    animation: fadeIn 0.5s ease-out;
  }

  /* 헤더 행: 타이틀 + 모드 버튼 같은 줄 */
  .saenggibu-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .saenggibu-page-title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.3;
  }

  /* 모드 토글 버튼 그룹 */
  .saenggibu-mode-toggle {
    display: flex;
    gap: 4px;
    background-color: #f3f4f6;
    padding: 3px;
    border-radius: 10px;
  }

  .saenggibu-mode-btn {
    padding: 7px 14px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  .saenggibu-mode-btn:hover {
    background-color: rgba(0,0,0,0.04);
  }

  .saenggibu-mode-btn--active-quick,
  .saenggibu-mode-btn--active-batch,
  .saenggibu-mode-btn--active-quick:hover,
  .saenggibu-mode-btn--active-batch:hover {
    background-color: #fff;
    color: #191f28;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .saenggibu-page-description {
    font-size: 14px;
    color: #6b7684;
    margin: 0;
    line-height: 1.5;
    text-align: left;
  }


  /* 에러 메시지 */
  .saenggibu-error-message {
    background: var(--whatquiz-red-50);
    border: 1px solid var(--whatquiz-red-200);
    color: var(--whatquiz-red-700);
    padding: var(--whatquiz-space-3) var(--whatquiz-space-4);
    border-radius: var(--whatquiz-radius-lg);
    margin-bottom: var(--whatquiz-space-6);
    font-size: var(--whatquiz-text-sm);
  }

  /* 미묘한 펄스 애니메이션 (항상 적용) */
  .subtle-pulse {
    animation: subtlePulse 3s ease-in-out infinite;
  }

  @keyframes subtlePulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
    50% { 
      transform: scale(1.02);
      box-shadow: 0 6px 16px rgba(49, 130, 246, 0.2);
    }
  }

  /* 매우 미묘한 펄스 (파일 첨부용 - 거의 못느낄 정도) */
  .very-subtle-pulse {
    animation: verySubtlePulse 4s ease-in-out infinite;
  }

  @keyframes verySubtlePulse {
    0%, 100% { 
      transform: scale(1);
      opacity: 1;
    }
    50% { 
      transform: scale(1.008);
      opacity: 0.95;
    }
  }

  /* 하이라이트 펄스 효과 (클릭 시) */
  .highlight-pulse {
    animation: highlightPulse 1s ease-out 2;
  }

  @keyframes highlightPulse {
    0%, 100% {
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(49, 130, 246, 0.3), 0 8px 24px rgba(49, 130, 246, 0.4);
      transform: scale(1.05);
    }
  }

  /* 비로그인 결과 미리보기 (Value First) */
  .sgb-preview-card {
    background: #f8f9fa;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 20px;
  }

  /* 반응형 */
  @media (max-width: 768px) {
    .saenggibu-page-container {
      padding: 8px 16px;
    }
    
    .saenggibu-header-row {
      gap: 8px;
    }

    .saenggibu-page-title {
      font-size: 20px;
    }
    
    .saenggibu-mode-btn {
      padding: 6px 10px;
      font-size: 13px;
    }

    .saenggibu-page-description {
      font-size: 13px;
    }

    .sgb-preview-card {
      padding: 16px;
    }

  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export default CreateSaenggibu; 